import {
  GAME_CONFIG,
  type GameConfig,
  type PremiumOfferConfig,
  type TaskAudienceConfig,
  type TaskDefinition,
} from "../config/gameConfig.ts";
import type {
  DailyTaskCode,
  GameState,
  PremiumOfferId,
  SeasonTaskCode,
  TaskMetric,
  TelegramCommunityTarget,
  UpgradeCode,
} from "./types.ts";

export type TaskProgress = {
  current: bigint;
  target: bigint;
  complete: boolean;
};

export type TaskAudienceContext = {
  readonly seasonDay: number;
  readonly confirmedTaps: bigint;
  readonly primaryProduced: bigint;
  readonly passiveProduced: bigint;
  readonly mechanicalLevel: number;
  readonly availableBalance: bigint;
  readonly arenaMatches: number;
  readonly hasAffordableUpgrade: boolean;
  readonly affordableUpgradePurchases: number;
  readonly communityTargets: Readonly<Record<TelegramCommunityTarget, boolean>>;
  readonly communityMemberships: Readonly<Record<TelegramCommunityTarget, boolean>>;
};

const EMPTY_TASK_AUDIENCE_CONTEXT: TaskAudienceContext = {
  seasonDay: 1,
  confirmedTaps: 0n,
  primaryProduced: 0n,
  passiveProduced: 0n,
  mechanicalLevel: 0,
  availableBalance: 0n,
  arenaMatches: 0,
  hasAffordableUpgrade: false,
  affordableUpgradePurchases: 0,
  communityTargets: { channel: false, chat: false },
  communityMemberships: { channel: false, chat: false },
};

function stableHash(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function isDefinitionActive(definition: { enabled: boolean; startsAt: string | null; endsAt: string | null }, now: Date): boolean {
  if (!definition.enabled) return false;
  const timestamp = now.getTime();
  const startsAt = definition.startsAt === null ? Number.NEGATIVE_INFINITY : new Date(definition.startsAt).getTime();
  const endsAt = definition.endsAt === null ? Number.POSITIVE_INFINITY : new Date(definition.endsAt).getTime();
  return timestamp >= startsAt && timestamp < endsAt;
}

function audienceAllows(audience: TaskAudienceConfig | null | undefined, context: TaskAudienceContext): boolean {
  if (audience == null) return true;
  if (audience.minSeasonDay !== undefined && context.seasonDay < audience.minSeasonDay) return false;
  if (audience.maxSeasonDay !== undefined && context.seasonDay > audience.maxSeasonDay) return false;
  if (audience.minConfirmedTaps !== undefined && context.confirmedTaps < BigInt(audience.minConfirmedTaps)) return false;
  if (audience.minPrimaryProduced !== undefined && context.primaryProduced < BigInt(audience.minPrimaryProduced)) return false;
  if (audience.minPassiveProduced !== undefined && context.passiveProduced < BigInt(audience.minPassiveProduced)) return false;
  if (audience.minMechanicalLevel !== undefined && context.mechanicalLevel < audience.minMechanicalLevel) return false;
  if (audience.minAvailableBalance !== undefined && context.availableBalance < BigInt(audience.minAvailableBalance)) return false;
  if (audience.minArenaMatches !== undefined && context.arenaMatches < audience.minArenaMatches) return false;
  if (audience.requiresAffordableUpgrade === true && !context.hasAffordableUpgrade) return false;
  if (
    audience.minAffordableUpgradePurchases !== undefined &&
    context.affordableUpgradePurchases < audience.minAffordableUpgradePurchases
  ) return false;
  if (audience.requiresCommunityTarget !== undefined && !context.communityTargets[audience.requiresCommunityTarget]) return false;
  if (audience.excludeIfCommunityMember !== undefined && context.communityMemberships[audience.excludeIfCommunityMember]) return false;
  return true;
}

export function taskAudienceContextFromState(
  state: GameState,
  now: Date,
  config: GameConfig = GAME_CONFIG,
): TaskAudienceContext {
  const levels: Readonly<Record<UpgradeCode, number>> = {
    precisionCutter: state.upgrades.precisionCutterLevel,
    mechanicalCutter: state.upgrades.mechanicalCutterLevel,
    energyCapacity: state.upgrades.energyCapacityLevel,
    energyRecovery: state.upgrades.energyRecoveryLevel,
  };
  const availableBalance = BigInt(state.account.available);
  const affordableUpgradePurchases = affordableUpgradePurchaseCount(levels, availableBalance, config);
  const hasAffordableUpgrade = affordableUpgradePurchases > 0;
  const start = new Date(state.season.startsAt).getTime();
  const seasonDay = Number.isFinite(start)
    ? Math.max(1, Math.min(config.balanceModel.seasonDays, Math.floor((now.getTime() - start) / 86_400_000) + 1))
    : 1;
  return {
    seasonDay,
    confirmedTaps: BigInt(state.progress.confirmedTaps),
    primaryProduced: BigInt(state.progress.primaryProduced),
    passiveProduced: BigInt(state.progress.passiveProduced),
    mechanicalLevel: state.upgrades.mechanicalCutterLevel,
    availableBalance,
    arenaMatches: state.progress.seasonal.arenaMatches,
    hasAffordableUpgrade,
    affordableUpgradePurchases,
    communityTargets: {
      channel: state.telegram.channel.available,
      chat: state.telegram.chat.available,
    },
    communityMemberships: {
      channel: state.telegram.channel.joined,
      chat: state.telegram.chat.joined,
    },
  };
}

export function affordableUpgradePurchaseCount(
  levels: Readonly<Record<UpgradeCode, number>>,
  availableBalance: bigint,
  config: GameConfig = GAME_CONFIG,
): number {
  const projectedLevels: Record<UpgradeCode, number> = { ...levels };
  let projectedBalance = availableBalance;
  let affordableUpgradePurchases = 0;
  while (true) {
    const candidates: { code: UpgradeCode; level: number; cost: bigint }[] = [];
    for (const code of Object.keys(config.upgrades) as UpgradeCode[]) {
      const track = config.upgrades[code];
      const ceiling = track.coreMaxLevel ?? track.levels.at(-1)?.level ?? track.initialLevel;
      const next = track.levels.find((level) => level.level === projectedLevels[code] + 1 && level.level <= ceiling);
      if (next?.costToReach != null) candidates.push({ code, level: next.level, cost: BigInt(next.costToReach) });
    }
    candidates.sort((left, right) =>
      left.cost === right.cost ? left.code.localeCompare(right.code) : left.cost < right.cost ? -1 : 1,
    );
    const next = candidates[0];
    if (next === undefined) break;
    const cost = next.cost;
    if (cost > projectedBalance) break;
    projectedBalance -= cost;
    projectedLevels[next.code] = next.level;
    affordableUpgradePurchases += 1;
  }
  return affordableUpgradePurchases;
}

export function assignDailyTaskCodes(
  userSeed: string,
  utcDay: string,
  now: Date,
  config: GameConfig = GAME_CONFIG,
  audienceContext: TaskAudienceContext = EMPTY_TASK_AUDIENCE_CONTEXT,
): [DailyTaskCode, DailyTaskCode, DailyTaskCode] {
  const active = config.liveOps.daily.tasks.filter(
    (definition) => isDefinitionActive(definition, now) && audienceAllows(definition.audience, audienceContext),
  );
  const forced = config.liveOps.daily.forcedTaskCode;
  const forcedDefinition = forced === null ? undefined : active.find((definition) => definition.code === forced);
  const priorityDefinitions = config.community.priorityDailyTaskCodes
    .map((code) => active.find((definition) => definition.code === code))
    .filter((definition): definition is TaskDefinition<DailyTaskCode> => definition !== undefined);
  const fixed = [
    ...(forcedDefinition === undefined ? [] : [forcedDefinition]),
    ...priorityDefinitions.filter((definition) => definition.code !== forcedDefinition?.code),
  ].slice(0, config.liveOps.daily.assignmentCount);
  const fixedCodes = new Set(fixed.map((definition) => definition.code));
  const candidates = active
    .filter((definition) => !fixedCodes.has(definition.code))
    .sort(
      (left, right) =>
        stableHash(`${userSeed}:${utcDay}:${left.code}`) - stableHash(`${userSeed}:${utcDay}:${right.code}`),
    );
  const required = config.liveOps.daily.assignmentCount - fixed.length;
  let best: typeof candidates | null = null;
  let bestMetricDiversity = -1;

  const choose = (
    start: number,
    selection: typeof candidates,
    compatibilityKeys: ReadonlySet<string>,
  ): void => {
    if (selection.length === required) {
      const combination = [...fixed, ...selection];
      const diversity = new Set(combination.map((definition) => definition.metric)).size;
      if (diversity > bestMetricDiversity) {
        best = [...selection];
        bestMetricDiversity = diversity;
      }
      return;
    }
    for (let index = start; index < candidates.length; index += 1) {
      const definition = candidates[index];
      if (definition === undefined) continue;
      const key = definition.compatibilityGroup ?? definition.code;
      if (compatibilityKeys.has(key)) continue;
      choose(index + 1, [...selection, definition], new Set([...compatibilityKeys, key]));
    }
  };

  choose(
    0,
    [],
    new Set(fixed.map((definition) => definition.compatibilityGroup ?? definition.code)),
  );
  if (best === null) throw new Error("Daily LiveOps catalog must provide three compatible active tasks");
  return [...fixed, ...best].map((definition) => definition.code) as [DailyTaskCode, DailyTaskCode, DailyTaskCode];
}

export function getDailyTaskDefinition(
  code: DailyTaskCode,
  config: GameConfig = GAME_CONFIG,
): TaskDefinition<DailyTaskCode> {
  const definition = config.liveOps.daily.tasks.find((candidate) => candidate.code === code);
  if (definition === undefined) throw new Error(`Missing daily task ${code}`);
  return definition;
}

export function getSeasonTaskDefinition(
  code: SeasonTaskCode,
  config: GameConfig = GAME_CONFIG,
): TaskDefinition<SeasonTaskCode> {
  const definition = config.liveOps.seasonTasks.find((candidate) => candidate.code === code);
  if (definition === undefined) throw new Error(`Missing season task ${code}`);
  return definition;
}

export function premiumOfferById(
  id: PremiumOfferId,
  config: GameConfig = GAME_CONFIG,
): PremiumOfferConfig {
  const offer = config.premium.offers.find((candidate) => candidate.id === id);
  if (offer === undefined) throw new Error(`Missing premium offer ${id}`);
  return offer;
}

export function isPremiumOfferAvailable(
  offer: PremiumOfferConfig,
  now: Date,
  seasonStartsAt?: string,
): boolean {
  return premiumOfferAvailability(offer, now, seasonStartsAt).active;
}

export type PremiumOfferAvailability = {
  readonly active: boolean;
  readonly startsAt: string | null;
  readonly endsAt: string | null;
  readonly phase: "upcoming" | "active" | "expired";
};

export function premiumOfferAvailability(
  offer: PremiumOfferConfig,
  now: Date,
  seasonStartsAt?: string,
): PremiumOfferAvailability {
  const timestamp = now.getTime();
  const absoluteStart = offer.startsAt === null ? Number.NEGATIVE_INFINITY : new Date(offer.startsAt).getTime();
  const absoluteEnd = offer.endsAt === null ? Number.POSITIVE_INFINITY : new Date(offer.endsAt).getTime();
  const relativeStart = offer.availableForSeasonSeconds === undefined || seasonStartsAt === undefined
    ? Number.NEGATIVE_INFINITY
    : new Date(seasonStartsAt).getTime();
  const relativeEnd = offer.availableForSeasonSeconds === undefined || seasonStartsAt === undefined
    ? Number.POSITIVE_INFINITY
    : relativeStart + offer.availableForSeasonSeconds * 1_000;
  const startsAt = Math.max(absoluteStart, relativeStart);
  const endsAt = Math.min(absoluteEnd, relativeEnd);
  const missingSeasonContext = offer.availableForSeasonSeconds !== undefined && seasonStartsAt === undefined;
  const active = !missingSeasonContext && timestamp >= startsAt && timestamp < endsAt;
  return {
    active,
    startsAt: Number.isFinite(startsAt) ? new Date(startsAt).toISOString() : null,
    endsAt: Number.isFinite(endsAt) ? new Date(endsAt).toISOString() : null,
    phase: active ? "active" : timestamp < startsAt ? "upcoming" : "expired",
  };
}

function metricValue(state: GameState, metric: TaskMetric, scope: "daily" | "season"): bigint {
  if (metric === "confirmed-taps") {
    return BigInt(scope === "daily" ? state.progress.daily.confirmedTaps : state.progress.confirmedTaps);
  }
  if (metric === "primary-produced") {
    return BigInt(scope === "daily" ? state.progress.daily.primaryProduced : state.progress.primaryProduced);
  }
  if (metric === "passive-produced") {
    return BigInt(scope === "daily" ? state.progress.daily.passiveProduced : state.progress.passiveProduced);
  }
  if (metric === "energy-spent") {
    return BigInt(scope === "daily"
      ? state.progress.daily.energySpent
      : state.progress.confirmedTaps * GAME_CONFIG.energy.costPerTap);
  }
  if (metric === "offline-collections") {
    return BigInt(scope === "daily" ? state.progress.daily.offlineCollections : 0);
  }
  if (metric === "return-visits") {
    return BigInt(scope === "daily" ? state.progress.daily.returnVisits : 0);
  }
  if (metric === "upgrade-purchases") {
    return BigInt(scope === "daily" ? state.progress.daily.upgradePurchases : state.progress.upgradePurchases);
  }
  if (metric === "arena-matches") {
    return BigInt(scope === "daily" ? state.progress.daily.arenaMatches : state.progress.seasonal.arenaMatches);
  }
  if (metric === "arena-wins") {
    return BigInt(scope === "daily" ? state.progress.daily.arenaWins : state.progress.seasonal.arenaWins);
  }
  if (metric === "pvp-matches") {
    return BigInt(scope === "daily" ? state.progress.daily.pvpMatches : state.progress.seasonal.pvpMatches);
  }
  if (metric === "daily-routes-claimed") return BigInt(state.progress.seasonal.dailyRoutesClaimed);
  if (metric === "active-invites") return BigInt(state.progress.seasonal.activeInvites);
  if (metric === "confirmed-invites") {
    return BigInt(scope === "daily" ? state.progress.daily.confirmedInvites : state.progress.seasonal.confirmedInvites);
  }
  if (metric === "channel-membership") return state.telegram.channel.joined ? 1n : 0n;
  return state.telegram.chat.joined ? 1n : 0n;
}

function progressForDefinition(
  state: GameState,
  definition: TaskDefinition<string>,
  scope: "daily" | "season",
): TaskProgress {
  const current = metricValue(state, definition.metric, scope);
  const target = BigInt(definition.target);
  return { current, target, complete: current >= target };
}

export function dailyTaskProgress(
  state: GameState,
  code: DailyTaskCode,
  config: GameConfig = GAME_CONFIG,
): TaskProgress {
  return progressForDefinition(state, getDailyTaskDefinition(code, config), "daily");
}

export function seasonTaskProgress(
  state: GameState,
  code: SeasonTaskCode,
  config: GameConfig = GAME_CONFIG,
): TaskProgress {
  return progressForDefinition(state, getSeasonTaskDefinition(code, config), "season");
}

export function seasonTaskUnlocked(
  state: GameState,
  code: SeasonTaskCode,
  config: GameConfig = GAME_CONFIG,
): boolean {
  const prerequisite = getSeasonTaskDefinition(code, config).prerequisiteCode;
  return prerequisite == null || state.progress.seasonal.claimedCodes.includes(prerequisite);
}

export function dailyGoalsCompletedCount(state: GameState, config: GameConfig = GAME_CONFIG): number {
  return state.progress.daily.assignmentCodes.filter((code) => dailyTaskProgress(state, code, config).complete).length;
}

export function dailyGoalsComplete(state: GameState, config: GameConfig = GAME_CONFIG): boolean {
  return dailyGoalsCompletedCount(state, config) === config.liveOps.daily.assignmentCount;
}

const GAME_DAY_MS = 86_400_000;

export function dailyRouteWindow(
  state: Pick<GameState, "season">,
  serverNow: string,
): { startsAt: string; resetsAt: string } {
  const now = Date.parse(serverNow);
  const seasonStartsAt = Date.parse(state.season.startsAt);
  const seasonEndsAt = Date.parse(state.season.endsAt);
  if (![now, seasonStartsAt, seasonEndsAt].every(Number.isFinite)) {
    return { startsAt: serverNow, resetsAt: serverNow };
  }
  if (now < seasonStartsAt) {
    const startsAt = new Date(seasonStartsAt).toISOString();
    return { startsAt, resetsAt: startsAt };
  }
  if (now >= seasonEndsAt) {
    const endsAt = new Date(seasonEndsAt).toISOString();
    return { startsAt: endsAt, resetsAt: endsAt };
  }
  const gameDayIndex = Math.floor((now - seasonStartsAt) / GAME_DAY_MS);
  const startsAt = seasonStartsAt + gameDayIndex * GAME_DAY_MS;
  const resetsAt = Math.min(startsAt + GAME_DAY_MS, seasonEndsAt);
  return {
    startsAt: new Date(startsAt).toISOString(),
    resetsAt: new Date(resetsAt).toISOString(),
  };
}

/**
 * Daily routes follow the season's relative 24-hour game days. The season can
 * start at any UTC time, so the next reset is not necessarily UTC midnight.
 * All inputs come from the authoritative server envelope.
 */
export function dailyRouteResetsAt(state: Pick<GameState, "season">, serverNow: string): string {
  return dailyRouteWindow(state, serverNow).resetsAt;
}

function previousUtcDay(utcDay: string): string {
  const date = new Date(`${utcDay}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}

export function nextDailyStreak(state: GameState): number {
  const lastClaimed = state.progress.dailyStreak.lastClaimedUtcDay;
  if (lastClaimed === state.progress.daily.utcDay) return state.progress.dailyStreak.current;
  if (lastClaimed === previousUtcDay(state.progress.daily.utcDay)) return state.progress.dailyStreak.current + 1;
  return 1;
}

export function dailyRouteReward(state: GameState, config: GameConfig = GAME_CONFIG): bigint {
  const streak = nextDailyStreak(state);
  return dailyRewardForStreak(streak, config);
}

export function dailyRewardForStreak(streak: number, config: GameConfig = GAME_CONFIG): bigint {
  const normalizedDay = Math.max(1, Math.min(Math.trunc(streak), config.liveOps.daily.rewardGrowthCapDay));
  const growth = BigInt(config.liveOps.daily.rewardDailyStep) * BigInt(normalizedDay - 1);
  const bonus = config.liveOps.daily.streakMilestones.find((milestone) => milestone.day === streak)?.bonus ?? "0";
  return BigInt(config.liveOps.daily.rewardBase) + growth + BigInt(bonus);
}

export function nextStreakMilestone(
  state: GameState,
  config: GameConfig = GAME_CONFIG,
): { day: number; bonus: string } | null {
  const current = state.progress.dailyStreak.current;
  return config.liveOps.daily.streakMilestones.find((milestone) => milestone.day > current) ?? null;
}
